import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '~/components/ui/buttons/Button';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import {
  useFailedJobOutboxEntriesPaginated,
  useRetryFailedJobOutboxEntry,
} from '~/api/jobOutbox';
import { formatDateTime } from '~/utils';

const PAGE_SIZE = 20;

const formatPayload = (payload: string): string => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

export default function AdminJobOutbox() {
  const { t } = useTranslation('job-outbox');

  const {
    data: failedEntries,
    sentinelRef,
    isLoading,
    pagination,
    removeItem,
  } = useFailedJobOutboxEntriesPaginated(PAGE_SIZE);

  const tableControls = useTable({});
  const { mutate: retryEntry } = useRetryFailedJobOutboxEntry();
  const [retryingIds, setRetryingIds] = useState<ReadonlySet<string>>(
    new Set()
  );

  const handleRetry = (id: string) => {
    setRetryingIds((previous) => new Set(previous).add(id));

    retryEntry(
      { id },
      {
        onSuccess: () => {
          removeItem(id);
          toast.success(t('toast.retrySuccess'));
        },
        onError: () => toast.error(t('toast.retryError')),
        onSettled: () =>
          setRetryingIds((previous) => {
            const next = new Set(previous);
            next.delete(id);
            return next;
          }),
      }
    );
  };

  const columns: TableColumn[] = [
    { key: 'payloadType', header: t('table.payloadType'), width: 18 },
    { key: 'payload', header: t('table.payload'), width: 26 },
    { key: 'lastError', header: t('table.lastError'), width: 24 },
    { key: 'attempts', header: t('table.attempts'), width: 8 },
    { key: 'failedAt', header: t('table.failedAt'), width: 14 },
    { key: 'retry', header: '', width: 10, stickTo: 'right' },
  ];

  const rows: TableRow[] = failedEntries.map((entry) => ({
    id: { value: entry.id },
    payloadType: { value: entry.payloadType },
    payload: {
      value: (
        <pre className="max-h-24 max-w-md overflow-auto whitespace-pre-wrap break-all text-xs font-mono text-neut-sec">
          {formatPayload(entry.payload)}
        </pre>
      ),
    },
    lastError: { value: entry.lastError ?? t('table.noError') },
    attempts: { value: entry.attempts },
    failedAt: {
      value: entry.failedAt ? formatDateTime(entry.failedAt) : '',
    },
    retry: {
      value: (
        <Button
          size="sm"
          width="auto"
          variant="secondary"
          loading={retryingIds.has(entry.id)}
          onClick={() => handleRetry(entry.id)}
          data-testid={`job-outbox-retry-${entry.id}`}
        >
          {t('table.retryButton')}
        </Button>
      ),
    },
  }));

  return (
    <div className="w-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-neut-prim">{t('title')}</h1>
        <p className="text-sm text-neut-sec mt-1">{t('subtitle')}</p>
      </div>

      <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
        {isLoading && failedEntries.length === 0 ? (
          <div className="p-8 text-center text-neut-sec text-sm">
            {t('table.loading')}
          </div>
        ) : (
          <Table
            minWidth={80}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
            emptyMessage={t('table.empty')}
            totalRowsCount={pagination.totalElements}
            name="job-outbox"
          />
        )}
        <div ref={sentinelRef} />
      </div>
    </div>
  );
}
